'use client';

import React, { useEffect, useRef, useState, FC } from 'react';
import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { MotionConfig, motion } from 'motion/react';
import clsx from 'clsx';
import addCommentMeta from '@/utils/post/comments/addCommentMeta';
import { toPersianDigits } from '@/utils/persianUtils';
import { useRequireLogin } from '@/components/(postTypes)/platforms/(archive)/layout/(review)/form/useRequireLogin';

interface LikeDislikeProps {
    commentId: number;
    likes: number;
    dislikes: number;
}

type Vote = 'like' | 'dislike' | '';

const storageKey = (id: number) => `user_like_dislike_${id}`;

interface VoteButtonProps {
    kind: 'like' | 'dislike';
    count: number;
    active: boolean;
    /** Bumped on each new vote of this kind: replays the pop and the «+۱». */
    pop: number;
    disabled: boolean;
    onClick: () => void;
}

const VoteButton: FC<VoteButtonProps> = ({ kind, count, active, pop, disabled, onClick }) => {
    const Icon = kind === 'like' ? ThumbsUp : ThumbsDown;
    const prevCount = useRef(count);
    const direction = count >= prevCount.current ? 1 : -1;
    useEffect(() => {
        prevCount.current = count;
    }, [count]);

    return (
        <motion.button
            type='button'
            onClick={onClick}
            disabled={disabled}
            aria-pressed={active}
            aria-label={`${kind === 'like' ? 'پسندیدم' : 'نپسندیدم'} (${count})`}
            whileTap={{ scale: 0.9 }}
            className={clsx(
                'relative flex items-center gap-1.5 rounded-md border px-2 py-1 transition-colors disabled:cursor-wait',
                active
                    ? kind === 'like'
                        ? 'border-green-500/40 bg-green-500/10 text-green-600 dark:text-green-400'
                        : 'border-red-500/40 bg-red-500/10 text-red-500 dark:text-red-400'
                    : 'border-gray-200 bg-gray-100 text-black hover:bg-gray-200 dark:border-white/10 dark:bg-white/15 dark:text-white/80 dark:hover:bg-white/25',
            )}
        >
            {/* the count slides in from the direction it changed */}
            <span className='inline-flex h-4 overflow-hidden tabular-nums leading-4'>
                <motion.span
                    key={count}
                    initial={pop ? { y: direction * 10, opacity: 0 } : false}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                    {toPersianDigits(String(count))}
                </motion.span>
            </span>
            <span className='relative'>
                <motion.span
                    key={pop}
                    className='block'
                    initial={false}
                    animate={
                        pop
                            ? {
                                  scale: [1, 1.35, 1],
                                  rotate: [0, kind === 'like' ? -14 : 14, 0],
                              }
                            : undefined
                    }
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                >
                    <Icon
                        size={16}
                        fill={active ? 'currentColor' : 'none'}
                        fillOpacity={active ? 0.25 : 0}
                    />
                </motion.span>
                {pop > 0 && active && (
                    <motion.span
                        key={`plus-${pop}`}
                        aria-hidden='true'
                        initial={{ opacity: 0.9, y: 0 }}
                        animate={{ opacity: 0, y: -14 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        className='pointer-events-none absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold'
                    >
                        +۱
                    </motion.span>
                )}
            </span>
        </motion.button>
    );
};

const LikeDislike: FC<LikeDislikeProps> = ({ commentId, likes, dislikes }) => {
    const [likesCount, setLikesCount] = useState<number>(likes);
    const [dislikesCount, setDislikesCount] = useState<number>(dislikes);
    const [vote, setVote] = useState<Vote>('');
    const [pop, setPop] = useState({ like: 0, dislike: 0 });
    const [busy, setBusy] = useState(false);
    const { ensureLogin, askLogin, dialog } = useRequireLogin(
        'برای پسندیدن یا نپسندیدن نظرها وارد حساب خود شوید.',
    );

    useEffect(() => {
        try {
            setVote((localStorage.getItem(storageKey(commentId)) as Vote) || '');
        } catch {
            /* storage blocked: start without a vote */
        }
    }, [commentId]);
    useEffect(() => setLikesCount(likes), [likes]);
    useEffect(() => setDislikesCount(dislikes), [dislikes]);

    // Click the active one again to take the vote back; click the other one to switch.
    // The counts change at once and go back if the server refuses.
    const handleVote = async (kind: 'like' | 'dislike') => {
        if (busy) return;
        if (!(await ensureLogin())) return;

        const before = { vote, likes: likesCount, dislikes: dislikesCount };
        const nextVote: Vote = vote === kind ? '' : kind;
        let nextLikes = likesCount - (vote === 'like' ? 1 : 0) + (nextVote === 'like' ? 1 : 0);
        let nextDislikes =
            dislikesCount - (vote === 'dislike' ? 1 : 0) + (nextVote === 'dislike' ? 1 : 0);
        nextLikes = Math.max(0, nextLikes);
        nextDislikes = Math.max(0, nextDislikes);

        setVote(nextVote);
        setLikesCount(nextLikes);
        setDislikesCount(nextDislikes);
        if (nextVote) setPop((p) => ({ ...p, [kind]: p[kind] + 1 }));
        setBusy(true);

        try {
            if (nextLikes !== before.likes) {
                await addCommentMeta(commentId, 'like', String(nextLikes));
            }
            if (nextDislikes !== before.dislikes) {
                await addCommentMeta(commentId, 'dislike', String(nextDislikes));
            }
            try {
                if (nextVote) localStorage.setItem(storageKey(commentId), nextVote);
                else localStorage.removeItem(storageKey(commentId));
            } catch {
                /* storage blocked */
            }
        } catch (error) {
            setVote(before.vote);
            setLikesCount(before.likes);
            setDislikesCount(before.dislikes);
            if ((error as { status?: number })?.status === 401) askLogin();
            else console.error('Comment vote failed:', error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <MotionConfig reducedMotion='user'>
            <div className={'flex items-center gap-x-3 text-xs'}>
                <VoteButton
                    kind='like'
                    count={likesCount}
                    active={vote === 'like'}
                    pop={pop.like}
                    disabled={busy}
                    onClick={() => handleVote('like')}
                />
                <VoteButton
                    kind='dislike'
                    count={dislikesCount}
                    active={vote === 'dislike'}
                    pop={pop.dislike}
                    disabled={busy}
                    onClick={() => handleVote('dislike')}
                />
            </div>
            {dialog}
        </MotionConfig>
    );
};

export default LikeDislike;
